import '../../_proto/landscope-map-viewer_pb';
import { grpcClientFactory } from './grpcClientFactory';
import { accountPlanFromProto, hasUnlimitedPlan } from '../utils/entitlements';
// canCreateAnotherWorkspace mirrors the server's plan gate so walls appear at
// the "+ New workspace" click, before any form. The server remains the enforcer.
export function canCreateAnotherWorkspace(workspaces) {
    const owned = workspaces.filter((w) => w.role === 'owner');
    return owned.length === 0 || owned.some(hasUnlimitedPlan);
}
/** Display name for a workspace, applying the unnamed default. */
export function workspaceDisplayName(w) {
    return w.name || 'My Workspace';
}
class WorkspaceService {
    constructor() {
        this.client = null;
    }
    createAuthenticatedClient() {
        if (!this.client) {
            this.client = grpcClientFactory.createWorkspaceClient({ enableCompression: true });
        }
        return this.client;
    }
    authMetadata() {
        const sessionId = localStorage.getItem('landscope_session_id');
        const sessionToken = localStorage.getItem('landscope_session_token');
        return { authorization: `Bearer ${sessionId}|${sessionToken}` };
    }
    workspaceFromProto(w) {
        return {
            id: w.getId(),
            name: w.getName(),
            role: w.getRole(),
            isActive: w.getIsActive(),
            activePlans: (w.getPlansList() || []).map(accountPlanFromProto),
        };
    }
    async listMyWorkspaces() {
        return new Promise((resolve, reject) => {
            const request = new proto.google.protobuf.Empty();
            this.createAuthenticatedClient().listMyWorkspaces(request, this.authMetadata(), (err, response) => {
                if (err) {
                    reject(new Error(`Failed to list workspaces: ${err.code} ${err.message}`));
                    return;
                }
                resolve(response.getWorkspacesList().map((w) => this.workspaceFromProto(w)));
            });
        });
    }
    async setActiveWorkspace(accountId) {
        return new Promise((resolve, reject) => {
            const request = new proto.landscope.mapviewer.v1.SetActiveWorkspaceRequest();
            request.setAccountId(accountId);
            this.createAuthenticatedClient().setActiveWorkspace(request, this.authMetadata(), (err, response) => {
                if (err) {
                    reject(new Error(`Failed to switch workspace: ${err.code} ${err.message}`));
                    return;
                }
                resolve(this.workspaceFromProto(response.getWorkspace()));
            });
        });
    }
    async inviteWorkspaceMember(args) {
        return new Promise((resolve, reject) => {
            const request = new proto.landscope.mapviewer.v1.InviteWorkspaceMemberRequest();
            request.setAccountId(args.accountId);
            request.setEmail(args.email);
            request.setRole(args.role);
            if (args.firstName)
                request.setFirstName(args.firstName);
            if (args.lastName)
                request.setLastName(args.lastName);
            if (args.acknowledgeSeatCharge)
                request.setAcknowledgeSeatCharge(true);
            this.createAuthenticatedClient().inviteWorkspaceMember(request, this.authMetadata(), (err, response) => {
                if (err) {
                    reject(new Error(`Failed to invite member: ${err.code} ${err.message}`));
                    return;
                }
                resolve({
                    success: response.getSuccess(),
                    message: response.getMessage(),
                    userWasCreated: response.getUserWasCreated(),
                    userNotFound: response.getUserNotFound(),
                });
            });
        });
    }
    async createWorkspace(name) {
        return new Promise((resolve, reject) => {
            const request = new proto.landscope.mapviewer.v1.CreateWorkspaceRequest();
            request.setName(name);
            this.createAuthenticatedClient().createWorkspace(request, this.authMetadata(), (err, response) => {
                if (err) {
                    reject(new Error(`Failed to create workspace: ${err.code} ${err.message}`));
                    return;
                }
                resolve(this.workspaceFromProto(response.getWorkspace()));
            });
        });
    }
    async renameWorkspace(accountId, name) {
        return new Promise((resolve, reject) => {
            const request = new proto.landscope.mapviewer.v1.RenameWorkspaceRequest();
            request.setAccountId(accountId);
            request.setName(name);
            this.createAuthenticatedClient().renameWorkspace(request, this.authMetadata(), (err, response) => {
                if (err) {
                    reject(new Error(`Failed to rename workspace: ${err.code} ${err.message}`));
                    return;
                }
                resolve(this.workspaceFromProto(response.getWorkspace()));
            });
        });
    }
    async removeWorkspaceMember(accountId, memberUserId) {
        return new Promise((resolve, reject) => {
            const request = new proto.landscope.mapviewer.v1.RemoveWorkspaceMemberRequest();
            request.setAccountId(accountId);
            request.setMemberUserId(memberUserId);
            this.createAuthenticatedClient().removeWorkspaceMember(request, this.authMetadata(), (err) => {
                if (err) {
                    reject(new Error(`Failed to remove member: ${err.code} ${err.message}`));
                    return;
                }
                resolve();
            });
        });
    }
    async listWorkspaceMembers(accountId) {
        return new Promise((resolve, reject) => {
            const request = new proto.landscope.mapviewer.v1.ListWorkspaceMembersRequest();
            request.setAccountId(accountId);
            this.createAuthenticatedClient().listWorkspaceMembers(request, this.authMetadata(), (err, response) => {
                if (err) {
                    reject(new Error(`Failed to list members: ${err.code} ${err.message}`));
                    return;
                }
                resolve(response.getMembersList().map((m) => ({
                    userId: m.getUserId(),
                    email: m.getEmail(),
                    firstName: m.getFirstName(),
                    lastName: m.getLastName(),
                    role: m.getRole(),
                    status: m.getStatus(),
                    grantedAt: m.getGrantedAt(),
                })));
            });
        });
    }
}
export const workspaceService = new WorkspaceService();
